import { fmtDateFull } from './dates';

/**
 * Opens a new window with a print-friendly rendering of a report,
 * then auto-triggers the print dialog. User saves as PDF.
 */
export function printReport({ title, columns, rows, subtitle }) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;

  const today = fmtDateFull(new Date());

  const tableRows = rows.map(r => `
    <tr>${columns.map(c => `<td>${formatCell(r[c.key], c)}</td>`).join('')}</tr>
  `).join('');

  const html = `<!doctype html>
<html lang="ar" dir="rtl">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  :root { --motif: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M 50,2 L 58.8,28.75 L 83.94,16.06 L 71.25,41.2 L 98,50 L 71.25,58.8 L 83.94,83.94 L 58.8,71.25 L 50,98 L 41.2,71.25 L 16.06,83.94 L 28.75,58.8 L 2,50 L 28.75,41.2 L 16.06,16.06 L 41.2,28.75 Z'/%3E%3C/svg%3E"); }
  @page { size: A4; margin: 15mm; }
  @font-face {
    font-family: 'Tajawal';
    src: url('/fonts/tajawal/Tajawal-Regular-arabic.woff2') format('woff2');
    font-weight: 400;
  }
  @font-face {
    font-family: 'Tajawal';
    src: url('/fonts/tajawal/Tajawal-Bold-arabic.woff2') format('woff2');
    font-weight: 700;
  }
  @font-face {
    font-family: 'Tajawal';
    src: url('/fonts/tajawal/Tajawal-Black-arabic.woff2') format('woff2');
    font-weight: 900;
  }
  body {
    font-family: 'Tajawal', 'Arial', sans-serif;
    direction: rtl;
    color: #1A2330;
    line-height: 1.9;
    margin: 0;
    padding: 20px;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding-bottom: 12px;
    border-bottom: 1px solid #E6E9EF;
    margin-bottom: 20px;
  }
  .header .kicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6B7686;
    letter-spacing: 0.04em;
    margin-bottom: 4px;
  }
  .header .kicker::before {
    content: '';
    width: 11px;
    height: 11px;
    background: #F2CA1B;
    -webkit-mask: var(--motif) center / contain no-repeat;
    mask: var(--motif) center / contain no-repeat;
  }
  .header h1 { margin: 0; font-size: 22px; font-weight: 900; color: #143A67; }
  .header .date { font-size: 13px; color: #6B7686; }
  .subtitle { font-size: 14px; color: #3A4452; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td {
    text-align: right;
    padding: 8px 10px;
    border: 1px solid #E6E9EF;
    vertical-align: middle;
  }
  th {
    background: #143A67;
    font-weight: 700;
    color: #fff;
  }
  tr:nth-child(even) td { background: #FAFCFE; }
  .badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
  }
  .b-success { background: #E6F2EC; color: #2E7D5B; }
  .b-warn    { background: #FDF4CE; color: #8A6D00; }
  .b-danger  { background: #F8E9E7; color: #C0392B; }
  .b-info    { background: #EAF2F8; color: #02537D; }
  .footer {
    position: fixed;
    bottom: 10mm;
    left: 15mm;
    right: 15mm;
    font-size: 10px;
    color: #6B7686;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #E6E9EF;
    padding-top: 6px;
  }
  @media print {
    body { padding: 0; }
    .no-print { display: none; }
    th, .badge {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
  .print-btn {
    position: fixed;
    top: 10px;
    left: 10px;
    padding: 10px 20px;
    background: #0E477A;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
  }
  .print-btn:hover { background: #143A67; }
  .empty { text-align: center; padding: 40px; color: #6B7686; }
</style>
</head>
<body>
<button class="no-print print-btn" onclick="window.print()">🖨 طباعة / حفظ PDF</button>
<div class="header">
  <div>
    <div class="kicker">منصة العمليات</div>
    <h1>${escapeHtml(title)}</h1>
  </div>
  <div class="date">${today}</div>
</div>
${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ''}
${rows.length === 0 ? `<div class="empty">لا توجد بيانات</div>` : `
<table>
  <thead>
    <tr>${columns.map(c => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr>
  </thead>
  <tbody>
    ${tableRows}
  </tbody>
</table>
`}
<div class="footer">
  <span>منصة العمليات</span>
  <span>إجمالي السجلات: ${rows.length}</span>
</div>
<script>
  window.addEventListener('load', () => {
    setTimeout(() => window.print(), 300);
  });
</script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatCell(value, col) {
  if (value == null || value === '') return '—';
  if (col.badge) {
    const v = Number(value);
    const cls = col.badge(v);
    return `<span class="badge b-${cls}">${escapeHtml(value)}</span>`;
  }
  if (col.transform) return escapeHtml(col.transform(value));
  return escapeHtml(value);
}
